import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AudioAsset, SfxEvent, SfxName } from "../../api";
import { AudioLibrary } from "./AudioLibrary";

/**
 * Sound-Effekt-Galerie analog zur Music-Gallery: scrollbare Liste aller SFX
 * mit Vorschau-Play und Schnell-Hinzufügen am Playhead. SFX werden als
 * unabhängige Timeline-Events (`SfxEvent`) gespeichert und können dann
 * verschoben / gestreckt / gelöscht werden.
 *
 * Die Liste ist nach UI-Sound-Familien gruppiert, damit man schnell findet
 * was man braucht.
 */

type SfxLibEntry = {
  id: SfxName;
  label: string;
  description: string;
  /** Geschätzte natürliche Länge in Sekunden — als Default-Bar-Länge. */
  defaultDurSec: number;
  emoji: string;
};

const SFX_GROUPS: { id: string; label: string; entries: SfxLibEntry[] }[] = [
  {
    id: "transitions",
    label: "Übergänge & Wipes",
    entries: [
      { id: "woosh",      label: "Woosh",        description: "Schneller Wipe / Slide", defaultDurSec: 0.6, emoji: "💨" },
      { id: "swoosh",     label: "Swoosh",       description: "Weicher Übergang",       defaultDurSec: 0.7, emoji: "🌬️" },
      { id: "transition", label: "Transition",   description: "Cinematischer Cut",      defaultDurSec: 0.9, emoji: "✨" },
      { id: "riser",      label: "Riser",        description: "Spannungsaufbau",        defaultDurSec: 1.6, emoji: "📈" },
    ],
  },
  {
    id: "impacts",
    label: "Impacts & Stops",
    entries: [
      { id: "impact",     label: "Impact",       description: "Harter Aufschlag",       defaultDurSec: 0.8, emoji: "💥" },
      { id: "thud",       label: "Thud",         description: "Dumpfer Bass-Stop",      defaultDurSec: 0.7, emoji: "🥊" },
      { id: "sub",        label: "Sub Boom",     description: "Tiefer Sub-Drop",        defaultDurSec: 1.2, emoji: "🔊" },
    ],
  },
  {
    id: "ui",
    label: "UI & Interaktion",
    entries: [
      { id: "click",      label: "Click",        description: "UI-Klick",               defaultDurSec: 0.3, emoji: "🖱️" },
      { id: "tap",        label: "Tap",          description: "Soft Tap",               defaultDurSec: 0.3, emoji: "👆" },
      { id: "tick",       label: "Tick",         description: "Counter-Tick",           defaultDurSec: 0.2, emoji: "⏱️" },
      { id: "pop",        label: "Pop",          description: "Bubble-Pop",             defaultDurSec: 0.4, emoji: "🫧" },
    ],
  },
  {
    id: "sparkle",
    label: "Highlights & Magic",
    entries: [
      { id: "ding",       label: "Ding",         description: "Erfolgs-Ding",           defaultDurSec: 0.7, emoji: "🔔" },
      { id: "notify",     label: "Notify",       description: "Slack-Style Notification", defaultDurSec: 0.6, emoji: "🔕" },
      { id: "shimmer",    label: "Shimmer",      description: "Glitzer-Sweep",          defaultDurSec: 1.3, emoji: "✨" },
      { id: "glass",      label: "Glass",        description: "Glas-Tap",               defaultDurSec: 0.6, emoji: "🥃" },
      { id: "magic",      label: "Magic",        description: "Sparkle / Reveal",       defaultDurSec: 1.0, emoji: "🪄" },
    ],
  },
  {
    id: "custom-drops",
    label: "Neu (Custom Drops)",
    entries: [
      { id: "sfx495",     label: "Drop 495",     description: "Custom SFX (sound-effect-495)", defaultDurSec: 2.1, emoji: "🆕" },
      { id: "sfx503",     label: "Drop 503",     description: "Custom SFX (sound-effect-503)", defaultDurSec: 2.1, emoji: "🆕" },
      { id: "sfx505",     label: "Drop 505",     description: "Custom SFX (sound-effect-505)", defaultDurSec: 2.1, emoji: "🆕" },
      { id: "sfx676",     label: "Drop 676",     description: "Custom SFX (sound-effect-676)", defaultDurSec: 4.1, emoji: "🆕" },
    ],
  },
];

/** Map id → entry, used by other components to look up default duration. */
const ALL_ENTRIES: Record<SfxName, SfxLibEntry> = SFX_GROUPS
  .flatMap((g) => g.entries)
  .reduce((acc, e) => {
    acc[e.id] = e;
    return acc;
  }, {} as Record<SfxName, SfxLibEntry>);

export function getSfxLibEntry(id: SfxName): SfxLibEntry {
  return ALL_ENTRIES[id];
}

/** MIME-Typ für Drag&Drop von der Gallery auf die Timeline. */
export const SFX_DRAG_MIME = "application/x-allyver-sfx";

type Props = {
  /** Aktuelle Position der Timeline (Sekunden) — Standard-Insert-Position. */
  playheadSec: number;
  /** Anzahl bestehender SFX-Events — für den Header-Counter. */
  sfxEventCount: number;
  /** Fügt einen neuen SFX-Event an `fromSec` hinzu. */
  onAdd: (sound: SfxName, fromSec: number, durSec: number) => void;
  /** Fügt einen Custom-Audio-SFX-Event hinzu (aus der Library). */
  onAddCustom?: (asset: AudioAsset, fromSec: number) => void;
  /** Reel-weite SFX-Master-Lautstärke (0..1). Default 1 wenn nicht gesetzt. */
  masterVolume?: number;
  /** Reel-weiter SFX-Master-Mute. Default false. */
  masterMuted?: boolean;
  /** Master-Lautstärke ändern (analog zur Music-Lautstärke). */
  onChangeMasterVolume?: (volume: number) => void;
  /** Master-Mute toggeln. */
  onToggleMasterMute?: () => void;
};

export const SfxGallery: React.FC<Props> = ({
  playheadSec,
  sfxEventCount,
  onAdd,
  onAddCustom,
  masterVolume = 1,
  masterMuted = false,
  onChangeMasterVolume,
  onToggleMasterMute,
}) => {
  const [filter, setFilter] = useState("");
  const [previewing, setPreviewing] = useState<SfxName | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Kleine Helfer: src für Preview (relativ zum public-Verzeichnis von Vite — der
  // Remotion-Renderer nutzt staticFile(), aber im Browser-UI greifen wir direkt
  // auf /sounds/<name>.wav zu, das vom selben Express-Server ausgeliefert wird).
  const previewUrl = useCallback((name: SfxName): string => `/sounds/${name}.wav`, []);

  const playPreview = useCallback(
    (name: SfxName) => {
      const url = previewUrl(name);
      // Re-use a single Audio element so we never stack overlapping previews.
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      const a = audioRef.current;
      a.pause();
      a.src = url;
      a.currentTime = 0;
      a.volume = 0.7;
      setPreviewing(name);
      a.play().catch(() => {
        /* Autoplay-Block oder Ladefehler — still ignorieren. */
      });
      a.onended = () => setPreviewing((p) => (p === name ? null : p));
    },
    [previewUrl],
  );

  // Stop preview when component unmounts.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return SFX_GROUPS;
    return SFX_GROUPS
      .map((g) => ({
        ...g,
        entries: g.entries.filter(
          (e) =>
            e.label.toLowerCase().includes(q) ||
            e.id.toLowerCase().includes(q) ||
            e.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.entries.length > 0);
  }, [filter]);

  const masterPct = Math.round(Math.max(0, Math.min(1, masterVolume)) * 100);

  return (
    <div className="side-panel sfx-panel">
      <div className="side-panel-head">
        <h4>🔊 Sound-Effekte</h4>
        {sfxEventCount > 0 ? (
          <span className="sfx-count-pill">{sfxEventCount} aktiv</span>
        ) : null}
      </div>

      {/* SFX-Master — analog zum Music-Volume-Slider. Wirkt auf ALLE SFX:
          die einzelnen Timeline-Events UND die in den Szenen eingebetteten
          Vorlagen-SFX. Ein Klick auf 🔇 mutet die ganze SFX-Spur, ohne dass
          die Audio-Tags aus dem Tree fliegen — die Musik bleibt sauber. */}
      {(onChangeMasterVolume || onToggleMasterMute) && (
        <div
          className="sfx-master-card"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            padding: "10px 12px",
            margin: "0 0 12px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700 }}>
              <button
                type="button"
                onClick={onToggleMasterMute}
                title={masterMuted ? "SFX aktivieren" : "Alle SFX stumm schalten"}
                aria-pressed={masterMuted}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: onToggleMasterMute ? "pointer" : "default",
                  fontSize: 16,
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                {masterMuted ? "🔇" : "🔊"}
              </button>
              <span>SFX-Master</span>
            </div>
            <span
              className="muted"
              style={{ fontSize: 11, opacity: masterMuted ? 0.5 : 0.85 }}
              title="Multiplier für ALLE SFX (Timeline + Vorlagen-SFX in den Szenen)"
            >
              {masterMuted ? "Stumm" : `${masterPct}%`}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={masterPct}
            disabled={!onChangeMasterVolume || masterMuted}
            onChange={(e) => onChangeMasterVolume?.(Number(e.target.value) / 100)}
            title="Master-Lautstärke aller Sound-Effekte"
            style={{ width: "100%", opacity: masterMuted ? 0.4 : 1 }}
          />
          <div className="muted" style={{ fontSize: 10.5, lineHeight: 1.4 }}>
            Wirkt auf alle Timeline-SFX <em>und</em> auf die in den Szenen eingebetteten
            Vorlagen-SFX. Mute zieht die Lautstärke auf 0 — die Musik läuft sauber weiter.
          </div>
        </div>
      )}

      <p className="muted" style={{ fontSize: 11.5, margin: "0 0 10px", lineHeight: 1.4 }}>
        Klick = Vorschau · ＋ = bei Playhead einfügen · auch direkt auf die Timeline ziehen.
        SFX werden zu eigenen Bars auf der „SFX"-Spur und können verschoben, gestreckt
        oder gelöscht werden.
      </p>

      <div className="sfx-section-label">Eigene Library</div>
      <AudioLibrary
        kindFilter="all"
        defaultKind="sfx"
        compact
        onSelect={onAddCustom ? (asset) => onAddCustom(asset, playheadSec) : undefined}
      />

      <div className="sfx-section-label" style={{ marginTop: 14 }}>Vorlagen-SFX</div>
      <input
        type="search"
        className="sfx-search"
        placeholder="Suchen (woosh, click, …)"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />

      <div className="sfx-gallery-list">
        {filteredGroups.map((g) => (
          <div key={g.id} className="sfx-group">
            <div className="sfx-group-label">{g.label}</div>
            <div className="sfx-grid">
              {g.entries.map((e) => {
                const isPlaying = previewing === e.id;
                return (
                  <div
                    key={e.id}
                    className={`sfx-card ${isPlaying ? "playing" : ""}`}
                    draggable
                    onDragStart={(ev) => {
                      ev.dataTransfer.setData(
                        SFX_DRAG_MIME,
                        JSON.stringify({ sound: e.id, defaultDurSec: e.defaultDurSec }),
                      );
                      ev.dataTransfer.effectAllowed = "copy";
                    }}
                  >
                    <button
                      type="button"
                      className="sfx-card-preview"
                      onClick={() => playPreview(e.id)}
                      title={`Vorschau abspielen — ${e.label}`}
                    >
                      <span className="sfx-card-emoji" aria-hidden>
                        {isPlaying ? "▶" : e.emoji}
                      </span>
                      <span className="sfx-card-info">
                        <span className="sfx-card-name">{e.label}</span>
                        <span className="sfx-card-desc">{e.description}</span>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="sfx-card-add"
                      onClick={() => onAdd(e.id, playheadSec, e.defaultDurSec)}
                      title={`Bei ${playheadSec.toFixed(1)}s einfügen`}
                    >
                      ＋
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {filteredGroups.length === 0 && (
          <div className="sfx-empty">Keine Treffer für „{filter}"</div>
        )}
      </div>
    </div>
  );
};

/* =====================================================================
 * SFX INSPECTOR — appears in the right sidebar when an SFX bar is selected.
 * Lets the user swap the sound, tweak volume / pitch, retime the event or
 * delete it. Mirrors the OverlayInspector pattern so the UX is consistent.
 * ===================================================================== */

type InspectorProps = {
  sfxEvent: SfxEvent;
  onChange: (patch: Partial<SfxEvent>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
};

export const SfxInspector: React.FC<InspectorProps> = ({
  sfxEvent,
  onChange,
  onDelete,
  onDuplicate,
}) => {
  const lib = ALL_ENTRIES[sfxEvent.sound];
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const isCustom = !!sfxEvent.src;
  const sourceUrl = sfxEvent.src ?? `/sounds/${sfxEvent.sound}.wav`;
  const displayLabel = sfxEvent.label ?? (isCustom ? "Custom Audio" : lib.label);
  const displayEmoji = isCustom ? "🎶" : lib.emoji;
  const displayDesc = isCustom ? "Eigener Upload" : lib.description;

  const playPreview = useCallback(() => {
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    a.pause();
    a.src = sourceUrl;
    a.currentTime = sfxEvent.trimStartSec ?? 0;
    a.volume = sfxEvent.volume ?? 0.6;
    a.playbackRate = sfxEvent.rate ?? 1;
    a.play().catch(() => {});
  }, [sourceUrl, sfxEvent.trimStartSec, sfxEvent.volume, sfxEvent.rate]);

  useEffect(() => () => { audioRef.current?.pause(); }, []);

  return (
    <div className="side-panel sfx-inspector">
      <div className="side-panel-head">
        <h4>🔊 SFX-Element</h4>
        <button
          type="button"
          className="btn ghost sm"
          onClick={playPreview}
          title="Vorschau abspielen"
        >
          ▶ Preview
        </button>
      </div>

      <div className="sfx-inspector-card">
        <div className="sfx-inspector-emoji">{displayEmoji}</div>
        <div className="sfx-inspector-meta">
          <div className="sfx-inspector-name">{displayLabel}</div>
          <div className="sfx-inspector-desc">{displayDesc}</div>
        </div>
      </div>

      {!isCustom && (
        <label className="form-row">
          <span>Sound</span>
          <select
            value={sfxEvent.sound}
            onChange={(e) => onChange({ sound: e.target.value as SfxName })}
          >
            {SFX_GROUPS.map((g) => (
              <optgroup key={g.id} label={g.label}>
                {g.entries.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.emoji} {e.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
      )}

      <label className="form-row">
        <span>Lautstärke ({Math.round((sfxEvent.volume ?? 0.6) * 100)}%)</span>
        <input
          type="range"
          min={0}
          max={100}
          value={Math.round((sfxEvent.volume ?? 0.6) * 100)}
          onChange={(e) => onChange({ volume: Number(e.target.value) / 100 })}
        />
      </label>

      <label className="form-row">
        <span>Pitch ({(sfxEvent.rate ?? 1).toFixed(2)}×)</span>
        <input
          type="range"
          min={50}
          max={200}
          value={Math.round((sfxEvent.rate ?? 1) * 100)}
          onChange={(e) => onChange({ rate: Number(e.target.value) / 100 })}
        />
      </label>

      <div className="form-row form-row-pair">
        <label>
          <span>Start (s)</span>
          <input
            type="number"
            step={0.1}
            min={0}
            value={Number(sfxEvent.fromSec.toFixed(2))}
            onChange={(e) => onChange({ fromSec: Math.max(0, Number(e.target.value)) })}
          />
        </label>
        <label>
          <span>Länge (s)</span>
          <input
            type="number"
            step={0.1}
            min={0.1}
            value={Number(sfxEvent.durSec.toFixed(2))}
            onChange={(e) => onChange({ durSec: Math.max(0.1, Number(e.target.value)) })}
          />
        </label>
      </div>

      <label className="form-row">
        <span>Trim-Start im Original ({(sfxEvent.trimStartSec ?? 0).toFixed(2)}s)</span>
        <input
          type="range"
          min={0}
          max={Math.max(0.1, sfxEvent.durSec * 4)}
          step={0.05}
          value={sfxEvent.trimStartSec ?? 0}
          onChange={(e) => onChange({ trimStartSec: Math.max(0, Number(e.target.value)) })}
        />
      </label>

      <div className="form-row form-row-pair">
        <label>
          <span>Fade-In ({(sfxEvent.fadeInSec ?? 0).toFixed(2)}s)</span>
          <input
            type="range"
            min={0}
            max={Math.max(0.1, sfxEvent.durSec / 2)}
            step={0.05}
            value={sfxEvent.fadeInSec ?? 0}
            onChange={(e) => onChange({ fadeInSec: Math.max(0, Number(e.target.value)) })}
          />
        </label>
        <label>
          <span>Fade-Out ({(sfxEvent.fadeOutSec ?? 0).toFixed(2)}s)</span>
          <input
            type="range"
            min={0}
            max={Math.max(0.1, sfxEvent.durSec / 2)}
            step={0.05}
            value={sfxEvent.fadeOutSec ?? 0}
            onChange={(e) => onChange({ fadeOutSec: Math.max(0, Number(e.target.value)) })}
          />
        </label>
      </div>

      <label className="form-row">
        <span>Eigener Name</span>
        <input
          type="text"
          placeholder={isCustom ? "Custom Audio" : lib.label}
          value={sfxEvent.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value || undefined })}
        />
      </label>

      <div className="sfx-inspector-actions">
        <button type="button" className="btn ghost sm" onClick={onDuplicate}>
          ⎘ Duplizieren
        </button>
        <button type="button" className="btn danger sm" onClick={onDelete}>
          ✕ Löschen
        </button>
      </div>
    </div>
  );
};
